<template>
  <div class="auth-page">
    <div class="auth-card">
      <h1 class="auth-title">Bem-vindo</h1>
      <p class="auth-subtitle">Entre na sua conta para continuar</p>

      <form @submit.prevent="handleSubmit" class="auth-form">
        <div class="form-group">
          <label for="email">Email</label>
          <input
            id="email"
            type="email"
            name="email"
            v-model="email"
            required
            autocomplete="email"
            placeholder="seu@email.com"
            @input="error = null"
          />
        </div>

        <div class="form-group">
          <label for="password">Senha</label>
          <div class="password-wrapper">
            <input
              id="password"
              :type="showPassword ? 'text' : 'password'"
              name="password"
              v-model="password"
              required
              autocomplete="current-password"
              placeholder="••••••••"
              @input="error = null"
            />
            <button
              type="button"
              class="password-toggle"
              :aria-label="showPassword ? 'Ocultar senha' : 'Mostrar senha'"
              @click="showPassword = !showPassword"
            >
              <i class="material-icons">{{ showPassword ? 'visibility_off' : 'visibility' }}</i>
            </button>
          </div>
        </div>

        <div v-if="error" class="error-message" role="alert">{{ error }}</div>

        <button type="submit" class="btn btn-primary btn-block" :disabled="submitLoading">
          <span v-if="submitLoading" class="btn-loading">
            <i class="material-icons spin">sync</i>
            Entrando...
          </span>
          <span v-else>Entrar</span>
        </button>
      </form>

      <div class="divider">
        <span>ou</span>
      </div>

      <button
        type="button"
        class="btn btn-google"
        @click="handleGoogleLogin"
        :disabled="loading"
      >
        <span class="google-icon">
          <svg viewBox="0 0 24 24" width="20" height="20">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
        </span>
        <span v-if="loading">Entrando...</span>
        <span v-else>Entrar com Google</span>
      </button>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue';
import { useUserStore } from '@/stores/user';
import { useRouter } from 'vue-router';
import { getAuthErrorMessage } from '@/utils/authErrors';

export default {
  name: 'Login',
  setup() {
    const email = ref('');
    const password = ref('');
    const error = ref(null);
    const loading = ref(false);
    const submitLoading = ref(false);
    const showPassword = ref(false);
    const userStore = useUserStore();
    const router = useRouter();

    const handleSubmit = async () => {
      error.value = null;
      submitLoading.value = true;
      try {
        await userStore.login({ email: email.value, password: password.value });
        router.push('/admin/');
      } catch (err) {
        error.value = getAuthErrorMessage(err);
      } finally {
        submitLoading.value = false;
      }
    };

    const handleGoogleLogin = async () => {
      error.value = null;
      loading.value = true;
      try {
        await userStore.loginWithGoogle();
        router.push('/admin/');
      } catch (err) {
        error.value = getAuthErrorMessage(err);
      } finally {
        loading.value = false;
      }
    };

    return {
      handleSubmit,
      handleGoogleLogin,
      email,
      password,
      error,
      loading,
      submitLoading,
      showPassword,
    };
  },
};
</script>

<style scoped>
.auth-page {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: calc(100vh - 56px);
  padding: var(--space-lg);
}

.auth-card {
  width: 100%;
  max-width: 400px;
  background: var(--color-surface);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md);
  border: 1px solid var(--color-border);
  padding: var(--space-xl);
}

.auth-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--color-text);
  margin: 0 0 var(--space-xs);
}

.auth-subtitle {
  font-size: 0.95rem;
  color: var(--color-text-secondary);
  margin: 0 0 var(--space-lg);
}

.auth-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
}

.btn-block {
  width: 100%;
  margin-top: var(--space-sm);
}

.password-wrapper {
  position: relative;
}

.password-wrapper input {
  padding-right: 48px;
}

.password-toggle {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  color: var(--color-text-muted);
  border-radius: var(--radius-sm);
}

.password-toggle:hover {
  color: var(--color-accent);
  background: var(--color-accent-soft);
}

.password-toggle .material-icons {
  font-size: 20px;
}

.btn-loading {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-sm);
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.btn-google {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-sm);
  width: 100%;
  background: var(--color-surface);
  color: var(--color-text);
  border: 1px solid var(--color-border);
  padding: 12px 16px;
  font-size: 1rem;
  font-family: inherit;
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: background 0.2s, box-shadow 0.2s;
}

.btn-google:hover:not(:disabled) {
  background: var(--color-bg);
  box-shadow: var(--shadow-sm);
}

.btn-google:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
